import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Users,
  UserCheck,
  UserX,
  UserPlus,
  Layers,
  Crown,
  Download,
  RefreshCw,
  LayoutGrid,
  List,
  Copy,
  Check,
  ChevronRight,
  ChevronLeft,
  ArrowUpDown,
  Sparkles,
  ShieldCheck,
  Phone,
  Briefcase
} from "lucide-react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../config";

// Preset color themes for visual distinction of groups
const COLOR_THEMES = [
  {
    id: "indigo",
    name: "Indigo Glow",
    gradient: "from-indigo-600 to-blue-600",
    badge: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-400 dark:border-indigo-500/30",
    pill: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",
    accent: "text-indigo-600 dark:text-indigo-400",
    bar: "bg-indigo-500",
    border: "border-indigo-200/70 dark:border-indigo-900/40",
    bgHover: "group-hover:border-indigo-400/50"
  },
  {
    id: "purple",
    name: "Purple Velvet",
    gradient: "from-purple-600 to-fuchsia-600",
    badge: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/30",
    pill: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
    accent: "text-purple-600 dark:text-purple-400",
    bar: "bg-purple-500",
    border: "border-purple-200/70 dark:border-purple-900/40",
    bgHover: "group-hover:border-purple-400/50"
  },
  {
    id: "emerald",
    name: "Emerald Peak",
    gradient: "from-emerald-600 to-teal-600",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30",
    pill: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    accent: "text-emerald-600 dark:text-emerald-400",
    bar: "bg-emerald-500",
    border: "border-emerald-200/70 dark:border-emerald-900/40",
    bgHover: "group-hover:border-emerald-400/50"
  },
  {
    id: "sky",
    name: "Ocean Sky",
    gradient: "from-sky-500 to-cyan-600",
    badge: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/30",
    pill: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
    accent: "text-sky-600 dark:text-sky-400",
    bar: "bg-sky-500",
    border: "border-sky-200/70 dark:border-sky-900/40",
    bgHover: "group-hover:border-sky-400/50"
  },
  {
    id: "amber",
    name: "Sunset Amber",
    gradient: "from-amber-500 to-orange-600",
    badge: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30",
    pill: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    accent: "text-amber-600 dark:text-amber-400",
    bar: "bg-amber-500",
    border: "border-amber-200/70 dark:border-amber-900/40",
    bgHover: "group-hover:border-amber-400/50"
  },
  {
    id: "rose",
    name: "Rose Quartz",
    gradient: "from-rose-500 to-pink-600",
    badge: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/30",
    pill: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300",
    accent: "text-rose-600 dark:text-rose-400",
    bar: "bg-rose-500",
    border: "border-rose-200/70 dark:border-rose-900/40",
    bgHover: "group-hover:border-rose-400/50"
  }
];

export default function UserGroups() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters, search & view settings
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("members-desc");
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("sb_user_groups_view") || "grid";
  });

  // Modal for Create / Edit Group
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [formData, setFormData] = useState({ groupName: "", colorTheme: "indigo" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Slide-over Drawer / Modal for Members Inspection
  const [inspectingGroup, setInspectingGroup] = useState(null);
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const [selectedAddUserId, setSelectedAddUserId] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);

  // Quick feedback
  const [copiedId, setCopiedId] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = viewMode === "grid" ? 12 : 10;

  // Persist view mode preference
  const handleViewChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem("sb_user_groups_view", mode);
    setCurrentPage(1);
  };

  // Initial fetch
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [groupsRes, usersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/groups/all`).then((r) => r.json()),
        fetch(`${API_BASE_URL}/api/users/all-users`)
          .then((r) => (r.ok ? r.json() : { status: false, data: [] }))
          .catch(() => ({ status: false, data: [] }))
      ]);

      if (groupsRes?.status && Array.isArray(groupsRes.data)) {
        setGroups(groupsRes.data);
      } else {
        setGroups([]);
      }

      if (usersRes?.status && Array.isArray(usersRes.data)) {
        setUsers(usersRes.data);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error("Error loading user groups data:", err);
      Swal.fire("Connection Error", "Failed to retrieve user groups. Please check your network.", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Helper: Match a user to a group reliably
  const doesUserBelongToGroup = (user, group) => {
    if (!user || !user.user_group || !group) return false;
    const uGrp = String(user.user_group).trim().toLowerCase();
    const gName = String(group.group_name || "").trim().toLowerCase();
    const gId = String(group.group_id_PK || "").trim().toLowerCase();

    if (uGrp === gName || uGrp === gId) return true;

    const cleanU = uGrp.replace(/^group\s+/, "");
    const cleanG = gName.replace(/^group\s+/, "");
    return cleanU === cleanG && cleanU.length > 0;
  };

  // Deterministic color theme for a group
  const getGroupTheme = (group, index = 0) => {
    const customTheme = localStorage.getItem(`sb_group_theme_${group.group_id_PK}`);
    if (customTheme) {
      const found = COLOR_THEMES.find((t) => t.id === customTheme);
      if (found) return found;
    }
    const idx = (group.group_id_PK || index) % COLOR_THEMES.length;
    return COLOR_THEMES[idx] || COLOR_THEMES[0];
  };

  // Pre-calculate members per group for fast rendering
  const groupMembersMap = useMemo(() => {
    const map = new Map();
    groups.forEach((grp) => {
      const members = users.filter((u) => doesUserBelongToGroup(u, grp));
      map.set(grp.group_id_PK, members);
    });
    return map;
  }, [groups, users]);

  // Overall KPIs
  const stats = useMemo(() => {
    const totalGroups = groups.length;
    let assignedCount = 0;

    users.forEach((u) => {
      const isAssigned = groups.some((g) => doesUserBelongToGroup(u, g));
      if (isAssigned) assignedCount++;
    });

    const unassignedCount = Math.max(0, users.length - assignedCount);

    let largestGrp = null;
    let maxMembers = -1;

    groups.forEach((g) => {
      const count = (groupMembersMap.get(g.group_id_PK) || []).length;
      if (count > maxMembers) {
        maxMembers = count;
        largestGrp = { ...g, memberCount: count };
      }
    });

    const percentAssigned = users.length > 0 ? Math.round((assignedCount / users.length) * 100) : 0;

    return {
      totalGroups,
      totalUsers: users.length,
      assignedCount,
      unassignedCount,
      percentAssigned,
      largestGroup: largestGrp && maxMembers > 0 ? largestGrp : null
    };
  }, [groups, users, groupMembersMap]);

  // Filtered & Sorted groups
  const filteredAndSortedGroups = useMemo(() => {
    let result = groups.filter((g) => {
      const nameMatch = g.group_name.toLowerCase().includes(searchTerm.toLowerCase());
      const idMatch = String(g.group_id_PK).includes(searchTerm.trim());
      return nameMatch || idMatch;
    });

    result.sort((a, b) => {
      const countA = (groupMembersMap.get(a.group_id_PK) || []).length;
      const countB = (groupMembersMap.get(b.group_id_PK) || []).length;

      switch (sortBy) {
        case "name-asc":
          return a.group_name.localeCompare(b.group_name);
        case "name-desc":
          return b.group_name.localeCompare(a.group_name);
        case "members-desc":
          return countB - countA;
        case "members-asc":
          return countA - countB;
        case "id-desc":
          return b.group_id_PK - a.group_id_PK;
        case "id-asc":
          return a.group_id_PK - b.group_id_PK;
        default:
          return 0;
      }
    });

    return result;
  }, [groups, searchTerm, sortBy, groupMembersMap]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedGroups.length / itemsPerPage) || 1;
  const paginatedGroups = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedGroups.slice(start, start + itemsPerPage);
  }, [filteredAndSortedGroups, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, viewMode]);

  // Handlers for Add/Edit
  const openModalForAdd = () => {
    setFormData({ groupName: "", colorTheme: "indigo" });
    setEditingGroupId(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (group) => {
    const savedTheme = localStorage.getItem(`sb_group_theme_${group.group_id_PK}`) || "indigo";
    setFormData({ groupName: group.group_name, colorTheme: savedTheme });
    setEditingGroupId(group.group_id_PK);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ groupName: "", colorTheme: "indigo" });
    setEditingGroupId(null);
  };

  const handleSaveGroup = async (e) => {
    if (e) e.preventDefault();
    const trimmedName = formData.groupName.trim();
    if (!trimmedName) {
      Swal.fire("Validation Error", "Please provide a valid group name.", "warning");
      return;
    }

    setIsSubmitting(true);
    const url = editingGroupId
      ? `${API_BASE_URL}/api/groups/update/${editingGroupId}`
      : `${API_BASE_URL}/api/groups/add`;

    try {
      const res = await fetch(url, {
        method: editingGroupId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ groupName: trimmedName })
      });
      const resJson = await res.json();

      if (resJson.status) {
        if (editingGroupId) {
          localStorage.setItem(`sb_group_theme_${editingGroupId}`, formData.colorTheme);
        }
        Swal.fire({
          icon: "success",
          title: editingGroupId ? "Group Updated!" : "Group Created!",
          text: resJson.message || `Group "${trimmedName}" is now active.`,
          timer: 1600,
          showConfirmButton: false
        });
        loadAllData(true);
        closeModal();
      } else {
        Swal.fire("Error", resJson.message || "Failed to save group.", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Network Error", "Unable to communicate with the server.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteGroup = (group) => {
    const members = groupMembersMap.get(group.group_id_PK) || [];
    const memberCount = members.length;

    let warningHtml = `<p class="text-sm text-slate-600 dark:text-slate-300">Are you sure you want to permanently delete <b>"${group.group_name}"</b>?</p>`;
    if (memberCount > 0) {
      warningHtml += `
        <div class="mt-3 p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-left text-xs text-amber-800 dark:text-amber-200">
          ⚠️ <b>Notice:</b> This group currently has <b>${memberCount} assigned user${memberCount > 1 ? "s" : ""}</b>. Deleting the group will leave them without an active cluster assignment.
        </div>
      `;
    }

    Swal.fire({
      title: "Delete Group?",
      html: warningHtml,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/groups/delete/${group.group_id_PK}`, {
            method: "DELETE"
          });
          const resJson = await res.json();
          if (resJson.status) {
            Swal.fire({
              icon: "success",
              title: "Deleted",
              text: `Group "${group.group_name}" has been removed.`,
              timer: 1400,
              showConfirmButton: false
            });
            localStorage.removeItem(`sb_group_theme_${group.group_id_PK}`);
            loadAllData(true);
            if (inspectingGroup?.group_id_PK === group.group_id_PK) {
              setInspectingGroup(null);
            }
          } else {
            Swal.fire("Delete Failed", resJson.message || "Could not delete group.", "error");
          }
        } catch (err) {
          console.error(err);
          Swal.fire("Error", "Network request failed.", "error");
        }
      }
    });
  };

  // Copy Group ID to clipboard
  const handleCopyId = (e, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(String(id));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  // Export Groups to Excel
  const handleExportGroups = () => {
    if (groups.length === 0) {
      Swal.fire("No Data", "There are no groups available to export.", "info");
      return;
    }

    const data = filteredAndSortedGroups.map((grp) => {
      const members = groupMembersMap.get(grp.group_id_PK) || [];
      const pct = stats.assignedCount > 0 ? Math.round((members.length / stats.assignedCount) * 100) : 0;
      return {
        "Group ID": grp.group_id_PK,
        "Group Name": grp.group_name,
        "Assigned Members": members.length,
        "Share of Assigned Users": `${pct}%`
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "UserGroups");
    XLSX.writeFile(workbook, `SocialBazar_User_Groups_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  // Export specific members of the inspected group
  const handleExportInspectedMembers = () => {
    if (!inspectingGroup) return;
    const members = groupMembersMap.get(inspectingGroup.group_id_PK) || [];
    if (members.length === 0) {
      Swal.fire("No Members", "No users belong to this group yet.", "info");
      return;
    }

    const data = members.map((u) => ({
      "User ID": u.user_id_PK || u.id,
      "Full Name": u.fullname || "N/A",
      "Mobile Number": u.mobile_number || "N/A",
      "Email": u.email || "N/A",
      "Business Name": u.business_name || u.businessName || "N/A",
      "Account Status": u.status || "Free",
      "Assigned Group": inspectingGroup.group_name
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, inspectingGroup.group_name.slice(0, 30));
    XLSX.writeFile(
      workbook,
      `Group_${inspectingGroup.group_name.replace(/[^a-zA-Z0-9]/g, "_")}_Members.xlsx`
    );
  };

  // Remove a single user from inspecting group
  const handleRemoveUserFromGroup = async (user) => {
    if (!inspectingGroup) return;

    Swal.fire({
      title: "Remove from Group?",
      text: `Remove ${user.fullname || "user"} from "${inspectingGroup.group_name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Remove",
      cancelButtonText: "Cancel"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const submitData = new FormData();
          submitData.append("id", user.user_id_PK || user.id);
          submitData.append("user_group", ""); // Clears the group assignment

          const res = await fetch(`${API_BASE_URL}/api/users/admin-update-user`, {
            method: "POST",
            body: submitData
          });
          const resJson = await res.json();

          if (resJson.status) {
            Swal.fire({
              icon: "success",
              title: "Removed",
              text: `${user.fullname || "User"} was removed from the group.`,
              timer: 1400,
              showConfirmButton: false
            });
            loadAllData(true);
          } else {
            Swal.fire("Error", resJson.message || "Failed to remove member.", "error");
          }
        } catch (err) {
          console.error(err);
          Swal.fire("Error", "Failed to update user record.", "error");
        }
      }
    });
  };

  // Add a user to inspecting group
  const handleAddUserToGroup = async () => {
    if (!inspectingGroup || !selectedAddUserId) return;

    const userToAdd = users.find((u) => String(u.user_id_PK || u.id) === String(selectedAddUserId));
    if (!userToAdd) return;

    setIsAssigning(true);
    try {
      const submitData = new FormData();
      submitData.append("id", userToAdd.user_id_PK || userToAdd.id);
      submitData.append("user_group", inspectingGroup.group_name);

      const res = await fetch(`${API_BASE_URL}/api/users/admin-update-user`, {
        method: "POST",
        body: submitData
      });
      const resJson = await res.json();

      if (resJson.status) {
        Swal.fire({
          icon: "success",
          title: "Member Added!",
          text: `${userToAdd.fullname || "User"} assigned to "${inspectingGroup.group_name}".`,
          timer: 1400,
          showConfirmButton: false
        });
        setSelectedAddUserId("");
        loadAllData(true);
      } else {
        Swal.fire("Error", resJson.message || "Could not assign user to group.", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update user record.", "error");
    } finally {
      setIsAssigning(false);
    }
  };

  // Inspected members list filtered by search
  const inspectedMembers = useMemo(() => {
    if (!inspectingGroup) return [];
    const members = groupMembersMap.get(inspectingGroup.group_id_PK) || [];
    if (!memberSearchTerm.trim()) return members;

    const q = memberSearchTerm.toLowerCase();
    return members.filter(
      (m) =>
        (m.fullname && m.fullname.toLowerCase().includes(q)) ||
        (m.mobile_number && m.mobile_number.includes(q)) ||
        (m.business_name && m.business_name.toLowerCase().includes(q)) ||
        (m.email && m.email.toLowerCase().includes(q))
    );
  }, [inspectingGroup, groupMembersMap, memberSearchTerm]);

  // Candidates for adding to current group (unassigned users prioritized)
  const candidateUsersToAdd = useMemo(() => {
    if (!inspectingGroup) return [];
    return users.filter((u) => !doesUserBelongToGroup(u, inspectingGroup));
  }, [inspectingGroup, users]);

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans relative overflow-x-hidden transition-colors duration-200">
      {/* Sidebar Navigation */}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Main Layout Area */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
        {/* Top Header */}
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="User Groups"
          subtitle="Segment your user base into clusters for targeted content delivery, permissions, and campaigns."
        />

        <main className="p-4 sm:p-6 lg:p-8 flex-1 flex flex-col gap-6  w-full mx-auto">
          {/* ─── Executive KPI Cards Row ─── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {/* 1. Total Groups */}
            <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-slate-800/80 p-5 border border-slate-200/80 dark:border-slate-700/60 shadow-sm hover:shadow-md transition-all duration-300 group">
              <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-indigo-500/10 dark:bg-indigo-500/5 group-hover:scale-125 transition-transform duration-500" />
              <div className="flex items-start justify-between relative z-10">
                <div>
                  <span className="text-xs font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                    Total Clusters
                  </span>
                  <h3 className="text-3xl font-extrabold text-slate-900 dark:text-white mt-1.5 tracking-tight">
                    {loading ? "..." : stats.totalGroups}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-indigo-600 dark:text-indigo-400">
                    <Layers className="w-3.5 h-3.5" />
                    <span>Active control segments</span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-indigo-500 to-blue-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/20 group-hover:scale-110 transition-transform duration-300">
                  <Layers className="w-6 h-6" />
                </div>
              </div>
            </div>

            {/* 2. Assigned Members */}
            <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-slate-800/80 p-5 border border-slate-200/80 dark:border-slate-700/60 shadow-sm hover:shadow-md transition-all duration-300 group">
              <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-emerald-500/10 dark:bg-emerald-500/5 group-hover:scale-125 transition-transform duration-500" />
              <div className="flex items-start justify-between relative z-10">
                <div>
                  <span className="text-xs font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                    Assigned Users
                  </span>
                  <h3 className="text-3xl font-extrabold text-slate-900 dark:text-white mt-1.5 tracking-tight">
                    {loading ? "..." : stats.assignedCount}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                    <UserCheck className="w-3.5 h-3.5" />
                    <span>
                      {stats.percentAssigned}% of total userbase
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-md shadow-emerald-500/20 group-hover:scale-110 transition-transform duration-300">
                  <UserCheck className="w-6 h-6" />
                </div>
              </div>
            </div>

            {/* 3. Unassigned Users */}
            <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-slate-800/80 p-5 border border-slate-200/80 dark:border-slate-700/60 shadow-sm hover:shadow-md transition-all duration-300 group">
              <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-amber-500/10 dark:bg-amber-500/5 group-hover:scale-125 transition-transform duration-500" />
              <div className="flex items-start justify-between relative z-10">
                <div>
                  <span className="text-xs font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                    Unassigned Pool
                  </span>
                  <h3 className="text-3xl font-extrabold text-slate-900 dark:text-white mt-1.5 tracking-tight">
                    {loading ? "..." : stats.unassignedCount}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">
                    <UserX className="w-3.5 h-3.5" />
                    <span>Pending allocation</span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-md shadow-amber-500/20 group-hover:scale-110 transition-transform duration-300">
                  <UserX className="w-6 h-6" />
                </div>
              </div>
            </div>

            {/* 4. Top Segment */}
            <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-slate-800/80 p-5 border border-slate-200/80 dark:border-slate-700/60 shadow-sm hover:shadow-md transition-all duration-300 group">
              <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-purple-500/10 dark:bg-purple-500/5 group-hover:scale-125 transition-transform duration-500" />
              <div className="flex items-start justify-between relative z-10">
                <div>
                  <span className="text-xs font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                    Largest Cluster
                  </span>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-2 truncate max-w-[150px]">
                    {loading ? "..." : stats.largestGroup ? stats.largestGroup.group_name : "None"}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-purple-600 dark:text-purple-400">
                    <Crown className="w-3.5 h-3.5" />
                    <span>
                      {stats.largestGroup ? `${stats.largestGroup.memberCount} members` : "No members yet"}
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-purple-500 to-pink-600 flex items-center justify-center text-white shadow-md shadow-purple-500/20 group-hover:scale-110 transition-transform duration-300">
                  <Crown className="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>

          {/* ─── Control Bar & Action Ribbon ─── */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 sm:p-5 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
            {/* Search & Sort */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
              {/* Search Bar */}
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by group name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900/60 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl pl-10 pr-9 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder:text-slate-400"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Sort Selector */}
              <div className="relative min-w-[170px]">
                <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-8 py-2.5 text-xs font-semibold outline-none focus:border-blue-500 cursor-pointer appearance-none transition-all"
                >
                  <option value="members-desc">Most Members</option>
                  <option value="members-asc">Fewest Members</option>
                  <option value="name-asc">Name (A → Z)</option>
                  <option value="name-desc">Name (Z → A)</option>
                  <option value="id-desc">Newest First</option>
                  <option value="id-asc">Oldest First</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 text-xs">
                  ▼
                </div>
              </div>
            </div>

            {/* View Switcher & Action CTAs */}
            <div className="flex items-center gap-2.5 self-end md:self-auto flex-wrap">
              {/* View Switcher (Grid vs Table) */}
              <div className="flex items-center bg-slate-100 dark:bg-slate-900/60 p-1 rounded-xl border border-slate-200 dark:border-slate-700/80">
                <button
                  onClick={() => handleViewChange("grid")}
                  title="Grid View"
                  className={`p-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${viewMode === "grid"
                      ? "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                    }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span className="hidden sm:inline">Grid</span>
                </button>
                <button
                  onClick={() => handleViewChange("table")}
                  title="Table View"
                  className={`p-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${viewMode === "table"
                      ? "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                    }`}
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">Table</span>
                </button>
              </div>

              {/* Refresh Button */}
              <button
                onClick={() => loadAllData(true)}
                disabled={refreshing}
                title="Refresh Groups"
                className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin text-blue-600" : ""}`} />
              </button>

              {/* Export Button */}
              <button
                onClick={handleExportGroups}
                title="Export Groups to Excel"
                className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/60 text-slate-700 dark:text-slate-200 text-xs font-semibold transition-all shadow-sm"
              >
                <Download className="w-4 h-4 text-slate-500" />
                <span className="hidden sm:inline">Export</span>
              </button>

              {/* Add Group CTA */}
              <button
                onClick={openModalForAdd}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                <Plus className="w-4 h-4" />
                <span>Create Group</span>
              </button>
            </div>
          </div>

          {/* ─── Main Content Display ─── */}
          {loading ? (
            /* Skeleton Loading Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3, 4, 5, 6].map((idx) => (
                <div
                  key={idx}
                  className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-700/60 animate-pulse flex flex-col gap-4 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-xl bg-slate-200 dark:bg-slate-700" />
                    <div className="w-16 h-5 rounded-full bg-slate-200 dark:bg-slate-700" />
                  </div>
                  <div className="w-3/4 h-6 rounded-lg bg-slate-200 dark:bg-slate-700 mt-2" />
                  <div className="w-1/2 h-4 rounded bg-slate-200 dark:bg-slate-700" />
                  <div className="w-full h-2 rounded-full bg-slate-100 dark:bg-slate-700/50 mt-4" />
                  <div className="pt-4 border-t border-slate-100 dark:border-slate-700/50 flex justify-between items-center">
                    <div className="w-24 h-8 rounded-lg bg-slate-200 dark:bg-slate-700" />
                    <div className="w-16 h-8 rounded-lg bg-slate-200 dark:bg-slate-700" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredAndSortedGroups.length === 0 ? (
            /* Empty State */
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 p-12 text-center flex flex-col items-center justify-center shadow-sm">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-blue-50 to-indigo-100 dark:from-slate-700 dark:to-slate-700/40 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4 shadow-inner">
                <Users className="w-10 h-10" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                {searchTerm ? "No Groups Match Your Search" : "No User Groups Created Yet"}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mt-1.5">
                {searchTerm
                  ? `No user group was found matching "${searchTerm}". Try resetting your search filters.`
                  : "Segment your users into groups to unlock targeted festival campaigns, business clusters, and automated permissions."}
              </p>
              <div className="mt-6 flex items-center gap-3">
                {searchTerm ? (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-semibold transition-colors"
                  >
                    Clear Search
                  </button>
                ) : (
                  <button
                    onClick={openModalForAdd}
                    className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:scale-[1.02]"
                  >
                    <Plus className="w-4 h-4" />
                    Create Your First Group
                  </button>
                )}
              </div>
            </div>
          ) : viewMode === "grid" ? (
            /* ─── Modern Grid / Card View ─── */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {paginatedGroups.map((group, index) => {
                const members = groupMembersMap.get(group.group_id_PK) || [];
                const theme = getGroupTheme(group, index);
                const percent =
                  stats.assignedCount > 0 ? Math.round((members.length / stats.assignedCount) * 100) : 0;

                return (
                  <div
                    key={group.group_id_PK}
                    className={`bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-700/60 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group relative overflow-hidden hover:-translate-y-1 ${theme.bgHover}`}
                  >
                    {/* Top Decorative Top Color Bar */}
                    <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${theme.gradient}`} />

                    <div>
                      {/* Top Row: Icon Badge, Group ID Chip, Actions */}
                      <div className="flex items-start justify-between gap-3 mb-3.5">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-11 h-11 rounded-xl bg-gradient-to-tr ${theme.gradient} text-white flex items-center justify-center shadow-md shadow-indigo-500/10 font-bold text-base group-hover:scale-105 transition-transform`}
                          >
                            {group.group_name.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span
                                onClick={(e) => handleCopyId(e, group.group_id_PK)}
                                title="Click to copy ID"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono font-medium bg-slate-100 hover:bg-slate-200 dark:bg-slate-700/60 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-pointer transition-colors"
                              >
                                #{group.group_id_PK}
                                {copiedId === group.group_id_PK ? (
                                  <Check className="w-2.5 h-2.5 text-emerald-500" />
                                ) : (
                                  <Copy className="w-2.5 h-2.5 opacity-60" />
                                )}
                              </span>
                            </div>
                            <h4 className="text-base font-bold text-slate-900 dark:text-white mt-0.5 tracking-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {group.group_name}
                            </h4>
                          </div>
                        </div>

                        {/* Top Action Pills (Edit / Delete) */}
                        <div className="flex items-center gap-1 opacity-90 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => openModalForEdit(group)}
                            title="Edit Group"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteGroup(group)}
                            title="Delete Group"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Member Statistics & Progress */}
                      <div className="my-4 bg-slate-50/70 dark:bg-slate-900/40 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5 text-slate-400" />
                            {members.length} {members.length === 1 ? "Member" : "Members"}
                          </span>
                          <span className="font-mono text-slate-400 text-[11px]">
                            {percent}% share
                          </span>
                        </div>

                        {/* Mini Progress Bar */}
                        <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700/60 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${theme.bar}`}
                            style={{ width: `${Math.min(100, Math.max(percent > 0 ? 6 : 0, percent))}%` }}
                          />
                        </div>
                      </div>

                      {/* Members Avatar Stack */}
                      <div className="flex items-center justify-between gap-2 pt-1 mb-2">
                        <span className="text-xs text-slate-400">Members Preview</span>
                        <div className="flex items-center -space-x-2 overflow-hidden">
                          {members.slice(0, 4).map((member, i) => (
                            <img
                              key={member.user_id_PK || member.id || i}
                              src={member.userimage || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.fullname || "User")}&background=random`}
                              alt={member.fullname || "User"}
                              className="w-7 h-7 rounded-full border-2 border-white dark:border-slate-800 object-cover bg-slate-200 shadow-xs"
                              onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.fullname || "User")}&background=random`;
                              }}
                            />
                          ))}
                          {members.length > 4 && (
                            <div className="w-7 h-7 rounded-full border-2 border-white dark:border-slate-800 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-200 flex items-center justify-center text-[10px] font-bold shadow-xs">
                              +{members.length - 4}
                            </div>
                          )}
                          {members.length === 0 && (
                            <span className="text-xs text-slate-400 italic">No users yet</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Card Footer CTA */}
                    <div className="pt-3.5 mt-2 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between gap-2">
                      <button
                        onClick={() => setInspectingGroup(group)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-slate-100 dark:bg-slate-700/70 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-300 text-slate-700 dark:text-slate-200 text-xs font-semibold transition-all"
                      >
                        <Users className="w-3.5 h-3.5" />
                        <span>View Members</span>
                        <ChevronRight className="w-3.5 h-3.5 opacity-60 ml-0.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ─── Executive Table View ─── */
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 shadow-sm overflow-hidden flex flex-col">
              <div className="overflow-x-auto min-h-[300px]">
                <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                  <thead>
                    <tr className="bg-slate-50/80 dark:bg-slate-900/60 border-b border-slate-200/80 dark:border-slate-700/60 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      <th className="py-4 px-6"># ID</th>
                      <th className="py-4 px-6">Group Name</th>
                      <th className="py-4 px-6">Members Count</th>
                      <th className="py-4 px-6">Avatar Preview</th>
                      <th className="py-4 px-6">% of Assigned</th>
                      <th className="py-4 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                    {paginatedGroups.map((group, index) => {
                      const members = groupMembersMap.get(group.group_id_PK) || [];
                      const theme = getGroupTheme(group, index);
                      const percent =
                        stats.assignedCount > 0
                          ? Math.round((members.length / stats.assignedCount) * 100)
                          : 0;

                      return (
                        <tr
                          key={group.group_id_PK}
                          className="hover:bg-slate-50/80 dark:hover:bg-slate-700/40 transition-colors group"
                        >
                          {/* ID Column */}
                          <td className="py-4 px-6 text-sm">
                            <span
                              onClick={(e) => handleCopyId(e, group.group_id_PK)}
                              title="Click to copy ID"
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-mono font-medium bg-slate-100 hover:bg-slate-200 dark:bg-slate-700/60 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-pointer transition-colors"
                            >
                              #{group.group_id_PK}
                              {copiedId === group.group_id_PK ? (
                                <Check className="w-3 h-3 text-emerald-500" />
                              ) : (
                                <Copy className="w-3 h-3 opacity-60" />
                              )}
                            </span>
                          </td>

                          {/* Group Name */}
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-8 h-8 rounded-lg bg-gradient-to-tr ${theme.gradient} text-white flex items-center justify-center font-bold text-xs shadow-xs`}
                              >
                                {group.group_name.slice(0, 2).toUpperCase()}
                              </div>
                              <span className="font-semibold text-sm text-slate-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                {group.group_name}
                              </span>
                            </div>
                          </td>

                          {/* Member Count Badge */}
                          <td className="py-4 px-6">
                            <span
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${theme.pill}`}
                            >
                              <Users className="w-3 h-3" />
                              {members.length} {members.length === 1 ? "User" : "Users"}
                            </span>
                          </td>

                          {/* Avatar Preview */}
                          <td className="py-4 px-6">
                            <div className="flex items-center -space-x-2">
                              {members.slice(0, 3).map((member, i) => (
                                <img
                                  key={member.user_id_PK || member.id || i}
                                  src={
                                    member.userimage ||
                                    `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                      member.fullname || "User"
                                    )}&background=random`
                                  }
                                  alt={member.fullname || "User"}
                                  className="w-7 h-7 rounded-full border-2 border-white dark:border-slate-800 object-cover bg-slate-200"
                                  onError={(e) => {
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                      member.fullname || "User"
                                    )}&background=random`;
                                  }}
                                />
                              ))}
                              {members.length > 3 && (
                                <div className="w-7 h-7 rounded-full border-2 border-white dark:border-slate-800 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-200 flex items-center justify-center text-[10px] font-bold">
                                  +{members.length - 3}
                                </div>
                              )}
                              {members.length === 0 && (
                                <span className="text-xs text-slate-400 italic">None</span>
                              )}
                            </div>
                          </td>

                          {/* Percentage Share */}
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2 max-w-[120px]">
                              <div className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${theme.bar}`}
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                              <span className="text-xs font-mono text-slate-500 dark:text-slate-400 min-w-[28px]">
                                {percent}%
                              </span>
                            </div>
                          </td>

                          {/* Action Buttons */}
                          <td className="py-4 px-6 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => setInspectingGroup(group)}
                                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors flex items-center gap-1"
                              >
                                <Users className="w-3.5 h-3.5" />
                                <span>Members</span>
                              </button>
                              <button
                                onClick={() => openModalForEdit(group)}
                                title="Edit Group"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteGroup(group)}
                                title="Delete Group"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ─── Modern Pagination Controls ─── */}
          {totalPages > 1 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl px-5 py-3.5 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Showing{" "}
                <span className="font-bold text-slate-800 dark:text-slate-200">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{" "}
                to{" "}
                <span className="font-bold text-slate-800 dark:text-slate-200">
                  {Math.min(currentPage * itemsPerPage, filteredAndSortedGroups.length)}
                </span>{" "}
                of{" "}
                <span className="font-bold text-slate-800 dark:text-slate-200">
                  {filteredAndSortedGroups.length}
                </span>{" "}
                groups
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span>Previous</span>
                </button>

                <div className="flex items-center gap-1 px-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                    .map((pageNum, idx, arr) => {
                      const showEllipsis = idx > 0 && pageNum - arr[idx - 1] > 1;
                      return (
                        <React.Fragment key={pageNum}>
                          {showEllipsis && <span className="text-xs text-slate-400 px-1">...</span>}
                          <button
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-7 h-7 rounded-lg text-xs font-bold transition-all ${currentPage === pageNum
                                ? "bg-blue-600 text-white shadow-xs"
                                : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                              }`}
                          >
                            {pageNum}
                          </button>
                        </React.Fragment>
                      );
                    })}
                </div>

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                >
                  <span>Next</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ─── Modal 1: Create / Edit Group Modal ─── */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-fade-in">
          <div className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl border border-slate-100 dark:border-slate-700 flex flex-col max-h-[92vh] animate-scale-up">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-700/80 flex justify-between items-center bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800/80">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  <Layers className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    {editingGroupId ? "Edit User Group" : "Create New User Group"}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {editingGroupId
                      ? "Rename and customize cluster properties"
                      : "Add a new cluster to categorize users and content"}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form Body */}
            <form onSubmit={handleSaveGroup} className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Group Name Field */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-2">
                  Group / Cluster Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    autoFocus
                    value={formData.groupName}
                    onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 bg-slate-50 dark:bg-slate-900/60 text-slate-900 dark:text-white transition-all font-medium placeholder:text-slate-400"
                    placeholder="E.g., VIP Platinum, Gujarat Retailers, North Zone"
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Users matching this name will automatically be grouped into this cluster.
                </p>
              </div>

              {/* Accent Color Theme Chooser */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-2">
                  Visual Accent Theme
                </label>
                <div className="grid grid-cols-3 gap-2.5">
                  {COLOR_THEMES.map((theme) => {
                    const isSelected = formData.colorTheme === theme.id;
                    return (
                      <button
                        type="button"
                        key={theme.id}
                        onClick={() => setFormData({ ...formData, colorTheme: theme.id })}
                        className={`flex items-center gap-2 p-2.5 rounded-xl border text-xs font-semibold transition-all ${isSelected
                            ? "border-blue-600 bg-blue-50/50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 ring-2 ring-blue-500/20 shadow-xs"
                            : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-slate-300"
                          }`}
                      >
                        <span className={`w-3.5 h-3.5 rounded-full bg-gradient-to-tr ${theme.gradient}`} />
                        <span className="truncate">{theme.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Real-time Badge Preview */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold block">
                    Live UI Preview
                  </span>
                  <div className="mt-2 flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${COLOR_THEMES.find((t) => t.id === formData.colorTheme)?.badge ||
                        COLOR_THEMES[0].badge
                        }`}
                    >
                      <Sparkles className="w-3 h-3" />
                      {formData.groupName.trim() || "Preview Name"}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-[11px] text-slate-400">Status</span>
                  <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mt-1 flex items-center justify-end gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Ready
                  </div>
                </div>
              </div>
            </form>

            {/* Modal Actions */}
            <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700/80 flex items-center justify-end gap-3 bg-slate-50/50 dark:bg-slate-900/40">
              <button
                type="button"
                onClick={closeModal}
                disabled={isSubmitting}
                className="px-4 py-2.5 text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveGroup}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-2.5 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-50"
              >
                {isSubmitting && <RefreshCw className="w-4 h-4 animate-spin" />}
                <span>{editingGroupId ? "Update Group" : "Create Group"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal 2: Slide-Over / Inspect Group Members Drawer ─── */}
      {inspectingGroup && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-slate-800 w-full max-w-xl h-full shadow-2xl border-l border-slate-200 dark:border-slate-700 flex flex-col animate-slide-left">
            {/* Drawer Header */}
            <div className="p-6 border-b border-slate-100 dark:border-slate-700/80 flex items-start justify-between bg-slate-50/50 dark:bg-slate-900/30">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono font-medium px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    Group #{inspectingGroup.group_id_PK}
                  </span>
                  <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
                    Active Cluster
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-1.5 flex items-center gap-2">
                  {inspectingGroup.group_name}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Viewing all members currently assigned to this content group.
                </p>
              </div>

              <button
                onClick={() => {
                  setInspectingGroup(null);
                  setMemberSearchTerm("");
                  setSelectedAddUserId("");
                }}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Sub-toolbar: Search & Quick Assign */}
            <div className="p-5 border-b border-slate-100 dark:border-slate-700/80 bg-white dark:bg-slate-800 flex flex-col gap-3">
              {/* Search Inside Group */}
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Filter members by name, phone, business..."
                  value={memberSearchTerm}
                  onChange={(e) => setMemberSearchTerm(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900/60 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-8 py-2 text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 placeholder:text-slate-400"
                />
                {memberSearchTerm && (
                  <button
                    onClick={() => setMemberSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Add User to This Group Ribbon */}
              <div className="flex items-center gap-2 pt-1">
                <div className="relative flex-1">
                  <select
                    value={selectedAddUserId}
                    onChange={(e) => setSelectedAddUserId(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-900/60 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-medium outline-none focus:border-blue-500 cursor-pointer appearance-none truncate"
                  >
                    <option value="">+ Assign user to this group...</option>
                    {candidateUsersToAdd.map((u) => (
                      <option key={u.user_id_PK || u.id} value={u.user_id_PK || u.id}>
                        {u.fullname || "Unnamed User"} ({u.mobile_number || "No Mobile"}) - Currently:{" "}
                        {u.user_group ? u.user_group : "Unassigned"}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 text-xs">
                    ▼
                  </div>
                </div>

                <button
                  onClick={handleAddUserToGroup}
                  disabled={!selectedAddUserId || isAssigning}
                  className="px-3.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-xs disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1.5 shrink-0"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  <span>{isAssigning ? "Assigning..." : "Assign"}</span>
                </button>
              </div>

              {/* Member Summary & Export */}
              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 pt-1">
                <span>
                  Showing <b>{inspectedMembers.length}</b> of{" "}
                  <b>{(groupMembersMap.get(inspectingGroup.group_id_PK) || []).length}</b> total members
                </span>
                <button
                  onClick={handleExportInspectedMembers}
                  className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-semibold"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Export List</span>
                </button>
              </div>
            </div>

            {/* Member List */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {inspectedMembers.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-700/50 text-slate-400 flex items-center justify-center mx-auto mb-3">
                    <Users className="w-7 h-7" />
                  </div>
                  <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">
                    {memberSearchTerm ? "No Matching Members" : "No Users Assigned to this Group"}
                  </h4>
                  <p className="text-xs text-slate-400 max-w-xs mx-auto mt-1">
                    {memberSearchTerm
                      ? "Try searching with a different name or mobile number."
                      : "Use the dropdown above to quickly assign users to this group, or assign users from the Users page."}
                  </p>
                </div>
              ) : (
                inspectedMembers.map((member) => (
                  <div
                    key={member.user_id_PK || member.id}
                    className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200/70 dark:border-slate-700/60 flex items-center justify-between gap-3 hover:border-slate-300 dark:hover:border-slate-600 transition-all group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={
                          member.userimage ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            member.fullname || "User"
                          )}&background=random`
                        }
                        alt={member.fullname || "User"}
                        className="w-10 h-10 rounded-xl object-cover bg-slate-200 dark:bg-slate-700 border border-white dark:border-slate-700 shrink-0 shadow-xs"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            member.fullname || "User"
                          )}&background=random`;
                        }}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h5 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                            {member.fullname || "Unnamed User"}
                          </h5>
                          {member.status === "Premium" && (
                            <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                              PRO
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                          {member.mobile_number && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3 opacity-60" />
                              {member.mobile_number}
                            </span>
                          )}
                          {member.business_name && (
                            <span className="flex items-center gap-1 truncate">
                              <Briefcase className="w-3 h-3 opacity-60" />
                              {member.business_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Remove Action */}
                    <button
                      onClick={() => handleRemoveUserFromGroup(member)}
                      title="Remove user from this group"
                      className="p-2 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Drawer Footer */}
            <div className="p-4 border-t border-slate-100 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-900/30 flex justify-end">
              <button
                onClick={() => {
                  setInspectingGroup(null);
                  setMemberSearchTerm("");
                  setSelectedAddUserId("");
                }}
                className="px-5 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold transition-colors"
              >
                Close Drawer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
